import Badge from '@components/common/badge/badge';
import clsx from 'clsx';
import { useContextSelector } from 'use-context-selector';
import { UserContext } from '@context/user.context';
import React, { useMemo, useRef, useState } from 'react';
import Button from '@components/atoms/button/button';
import { SizeCategory } from '@interfaces/models/mySizes';
import { TFunction, useTranslation } from 'next-i18next';
import filterGroupConfigurations from '@config/catalog/filter-group-configurations';
import styles from './mysizes-badges.module.scss';

const sizeToLabel: { [key: string]: string } = {
  '1': 'MYSIZES.SIZE_CATEGORY.SIZE1',
  '3': 'MYSIZES.SIZE_CATEGORY.SIZE3',
  '4': 'MYSIZES.SIZE_CATEGORY.SIZE4',
  '10': 'MYSIZES.SIZE_CATEGORY.SIZE10',
  '21': 'MYSIZES.SIZE_CATEGORY.SIZE21',
};

interface SizeWithLabel extends SizeCategory {
  sizeLabel: string[];
  universeId: string;
}

const getBadgeLabels = (sizesWithLabels: SizeWithLabel[], unverseId: string) => {
  return sizesWithLabels
    .filter((category) => category.universeId === unverseId)
    .map((category) => ({ ...category, badgeLabel: category.sizeLabel.join(', ') }))
    .filter((category) => category.badgeLabel.length > 0)
    .sort((a, b) => a.badgeLabel.length - b.badgeLabel.length);
};

const renderBadges = (sizesWithLabels: SizeWithLabel[], universeId: string, t: TFunction) => {
  const badgeLabels = getBadgeLabels(sizesWithLabels, universeId);
  if (badgeLabels.length === 0) {
    return <span className={styles.mysizes_badges__no_sizes}>{t('MYSIZES.FILTER_SET.NO_SIZES')}</span>;
  }
  return badgeLabels.map((category, i) => (
    <Badge
      badgeSize="medium"
      key={`mysizes_badge_${i}`}
      className={clsx(styles.mysizes_badges__badge)}
      isInteractive={false}
    >
      {`${category.label}: ${category.badgeLabel}`}
    </Badge>
  ));
};

interface MySizesBadgesProps {
  handleOpenEditDialog: () => void;
  editBtnClassName: string;
}

const MySizesBadges: React.FC<MySizesBadgesProps> = ({ handleOpenEditDialog, editBtnClassName }) => {
  const userSizes = useContextSelector(UserContext, (v) => v.userSizes);
  const containerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState<boolean>(false);
  const { t } = useTranslation();

  const sizesWithLabels: SizeWithLabel[] = useMemo(
    () =>
      userSizes?.size_categories?.map((category) => {
        const universeId =
          userSizes.universe.ids?.length === 1
            ? userSizes.universe.ids[0]
            : filterGroupConfigurations.find((x) => x.id === category.id)?.universeId;
        return {
          ...category,
          label: t(sizeToLabel[category.id]) || '',
          sizeLabel: category.sizes.map((size) => size.label),
          universeId,
        };
      }) || [],
    [userSizes, t],
  );

  const getHeader = (universeId) => {
    if (userSizes.universe.ids?.length === 1 || expanded) {
      return universeId === '1' ? 'MYSIZES.SIZE_CATEGORY.UNIVERSE1' : 'MYSIZES.SIZE_CATEGORY.UNIVERSE2';
    }
    return universeId === '1' ? 'MYSIZES.SIZE_CATEGORY.UNIVERSE12' : 'MYSIZES.SIZE_CATEGORY.UNIVERSE2';
  };

  const badgesContentId = 'mysizes-badges-content';

  return (
    <>
      <div
        className={clsx(styles.mysizes_badges)}
        style={expanded ? { maxHeight: containerRef.current.offsetHeight } : undefined}
        id={badgesContentId}
      >
        <div
          ref={containerRef}
          className={clsx(styles.mysizes_badges__container)}
          data-cy="mySizes-Badges"
        >
          {userSizes.universe.ids?.map((universeId) => (
            <React.Fragment key={`mysizes_fragment_${universeId}`}>
              <div className={styles.mysizes_badges__header}>{t(getHeader(universeId))}</div>
              <div className={styles.mysizes_badges__badges_section}>
                {renderBadges(sizesWithLabels, universeId, t)}
              </div>
            </React.Fragment>
          ))}
          <Button
            type="button"
            size="medium"
            className={editBtnClassName}
            onClick={handleOpenEditDialog}
            iconLeft="edit-16"
            tabIndex={expanded ? 0 : -1}
            aria-label={t('MYSIZES.FILTER_SET_EDIT.CTA')}
            data-cy="mysizes-edit-sizes-button"
          >
            {t('MYSIZES.FILTER_SET_EDIT.CTA')}
          </Button>
        </div>
      </div>
      <Button
        onClick={() => setExpanded((prev) => !prev)}
        disableDefaultStyling
        className={styles.mysizes_badges__expandable}
        aria-expanded={expanded}
        aria-controls={badgesContentId}
        data-cy="mysizes-badges-expand-button"
      >
        {expanded ? t('MYSIZES.FILTER_SET.COLLAPSE') : t('MYSIZES.FILTER_SET.EXPAND')}
      </Button>
    </>
  );
};

export default MySizesBadges;
